import { Observable } from 'rxjs';
import { GlobalPositionStrategy } from '@angular/cdk/overlay';
import { DialogConfig } from '@angular/cdk/dialog';

import {
  Directive,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { BaseOverlay } from '@shared/components/base-overlay/base-overlay.component';
import { SidenavConfig } from '@shared/modules/sidenav/models/sidenav.models';

@Directive({
  selector: '[zefSidenav]',
  standalone: false,
})
export class SidenavDirective<T = any> extends BaseOverlay<T> implements OnChanges, OnInit {
  private tr = inject<TemplateRef<any>>(TemplateRef);

  private readonly defaultWidth = 800;

  private readonly defaultPosition: 'left' | 'right' = 'right';

  private readonly defaultPanelClass = 'zef-sidenav';

  private readonly defaultBackdropClass = 'zef-sidenav-backdrop';

  private readonly scrimBackdropClass = 'cdk-overlay-dark-backdrop';

  private readonly transparentBackdropClass = 'cdk-overlay-transparent-backdrop';

  config: DialogConfig = {
    hasBackdrop: true,
    positionStrategy: new GlobalPositionStrategy().top('0').bottom('0'),
    maxWidth: '100vw',
    height: '100%',
    panelClass: this.defaultPanelClass,
    backdropClass: this.defaultBackdropClass as any,
    autoFocus: true,
    disableClose: true,
  };

  @Output('zefSidenavClose')
  readonly close = new EventEmitter<T>();

  @Output('zefSidenavAfterClosed')
  readonly afterClosed = new EventEmitter<void>();

  @Input('zefSidenavPosition')
  position: 'left' | 'right' = this.defaultPosition;

  @Input('zefSidenavWidth')
  width: number | string = this.defaultWidth;

  @Input('zefSidenavScrim')
  scrim: boolean = true;

  @Input('zefSidenavBackdrop')
  backdrop: boolean = true;

  @Input('zefSidenav')
  open?: boolean;

  @Input('zefWaitClose')
  waitClose?: Observable<boolean>;

  @Input('zefSidenavConfig')
  sidenavConfig?: SidenavConfig;

  private widthAnimationTimer?: number;

  get content(): TemplateRef<any> {
    return this.tr;
  }

  closeSidenav(value?: T): void {
    this.closeDialog(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config) {
      this.config.viewContainerRef = this.sidenavConfig.viewContainerRef;
    }

    if (changes.width) {
      this.setWidth();
    }

    if (changes.position) {
      this.setPosition();
    }

    if (changes.scrim) {
      this.setScrim();
    }

    if (changes.backdrop) {
      this.setBackdrop();
    }

    super.ngOnChanges(changes);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setWidth();
    this.setPosition();
    this.setScrim();
  }

  setWidth(): void {
    this.width = this.width ?? this.defaultWidth;
    this.config.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
  }

  animateWidth(width: number | string, transitionTime = 200): void {
    if (this.dialogReference) {
      const panel = this.dialogReference.overlayRef.overlayElement;

      if (panel) {
        window.clearTimeout(this.widthAnimationTimer);
        panel.style.transition = `${transitionTime}ms width ease-in-out`;
        this.width = width;
        this.setWidth();

        this.widthAnimationTimer = window.setTimeout(() => (panel.style.transition = null), transitionTime);
      }
    }
  }

  private setPosition(): void {
    this.position = this.position || this.defaultPosition;

    this.config.positionStrategy = new GlobalPositionStrategy().top('0')[this.position]('0');
    this.setClasses();
  }

  private setScrim(): void {
    this.setClasses();
  }

  private setBackdrop(): void {
    this.config.hasBackdrop = !!this.backdrop;
  }

  private setClasses(): void {
    this.config.backdropClass = [
      this.defaultBackdropClass,
      this.scrim ? this.scrimBackdropClass : this.transparentBackdropClass,
    ] as any;

    this.config.panelClass = [this.defaultPanelClass, this.position];
  }
}
